import React, { useState, useEffect, useRef, useCallback } from 'react'
import { transformHtmlContent } from 'util/transformHtmlContent';
import { useUser } from 'util/UserContext'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import api from 'config/axiosConfig'
import dayjs, { formatRelativeTime } from 'config/dayjsConfig';
import RenderComments from './RenderComments'
import CommentInput from './CommentInput'
import Save from '../Entities/Save'
import './Comment.css'
import EntityActions from '../Entities/EntityActions'
import Report from 'components/Entities/Report'
import { Comment as CommentData } from 'util/constants'
import { Box, Avatar, Tooltip } from '@mui/joy'
import { BookCheck, Ellipsis, Flag, Link as LinkIcon } from 'lucide-react';
//import 'components/Entities/EntityActions.css'
import { Pencil } from 'lucide-react';
import { Trash } from 'lucide-react';
import { CirclePlus } from 'lucide-react';
import { CircleMinus } from 'lucide-react';
import { MessageSquareReply } from 'lucide-react';
import { 
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'ui/DropdownMenu'
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer'
import DialogConfirmation from 'ui/DialogConfirmation'
import { toast } from 'sonner';
import { PrivacyLevel } from 'util/userUtils';
import EntityPrivacy from 'components/Entities/EntityPrivacy';
import { createPortal } from 'react-dom';
import { Coauthor, CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import CoauthorsModal from 'components/Common/CoauthorsModal';
interface CommentsProps extends CommentData {
    depth: number
    maxDepth: number
    isAllCollapsed: boolean
    isEntitySection: boolean
    onAddReply: (parent_id: number, content: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[]) => void
    onUpdateComment: (id: number, content: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[], addedCoauthors: CoauthorSuggestion[], removedCoauthors: CoauthorSuggestion[]) => void
    onDeleteComment: (id: number) => void
}

const Comment: React.FC<CommentsProps> = ({
    id,
    id_short,
    username,
    properties_public,
    user_id,
    user_id_short,
    created_at,
    content,
    properties,
    replies,
    commentable_type,
    commentable_id,
    commentable_id_short,
    upvotes,
    downvotes,
    vote_count,
    parent_id,
    parent_id_short,
    root_id,
    root_id_short,
    updated_at,
    is_active,
    depth = 0,
    maxDepth = 6,
    isAllCollapsed = false,
    isEntitySection = false,
    viewer_relation,
    onAddReply,
    onUpdateComment,
    onDeleteComment,
}) => {
    const { currentUser, setShowAuthModal, userIsMobile } = useUser()
    const [isAuthor, setIsAuthor] = useState<boolean>(false)
    const [isCollapsed, setIsCollapsed] = useState(false)
    const [isReplying, setIsReplying] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const moreIconRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const reportModalRef = useRef<HTMLDivElement>(null)
    const hasReplies = replies && replies.length > 0
    const location = useLocation()
    const navigate = useNavigate()
    const entity = {
        id,
        id_short,
        name: null,
        eid: null,
        username,
        user_id,
        user_id_short,
        upvotes,
        downvotes,
        vote_count,
        content,
        commentable_type,
        commentable_id,
        commentable_id_short,
        created_at,
        is_active,
        replies,
        parent_id,
        parent_id_short,
        root_id,
        root_id_short,
        updated_at,
        properties: null,
        properties_public: properties_public,
        attributes: null,
        commentable_entity: undefined,
        viewer_relation: null,
    }

    const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN;

    const [isLoadingIssueName, setIsLoadingIssueName] = useState(false);

    const [commentAction, setCommentAction] = useState<'edit' | 'reply' | null>(null);
    const [coauthors, setCoauthors] = useState<Coauthor[]>(properties?.coauthors || [])
    const [newCoauthors, setNewCoauthors] = useState<CoauthorSuggestion[]>([])
    const [removedCoauthors, setRemovedCoauthors] = useState<CoauthorSuggestion[]>([])
    const [showCoauthorsModal, setShowCoauthorsModal] = useState(false)

    const getIssueNameById = useCallback(async (id: number) => {
        try {
            const response = await api.get(`/api/issues/id/${id}`);
            return response.data.name.replace(/ /g, '_');
        } catch (error) {
            console.error('Error fetching issue name:', error);
            return null;
        }
    }, []);

    useEffect(() => {
        setIsAuthor(currentUser?.username === username)
    }, [currentUser, username])

    useEffect(() => {
        setIsCollapsed(depth >= maxDepth || isAllCollapsed)
    }, [depth, maxDepth, isAllCollapsed])

    const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const element = event.target as HTMLElement;
        
        // Check if the clicked element or any of its parents have the ignore-card-click class
        const shouldIgnoreClick = element.closest('.ignore-card-click') !== null;
        
        // Return early if it's a link, read-more, or should be ignored
        if (element.tagName.toLowerCase() === 'a' || 
            element.classList.contains('read-more') || 
            shouldIgnoreClick) {
            return;
        }

        // Only proceed with navigation if not in entity section
        if (!isEntitySection) {
            setIsLoadingIssueName(true);
            let path;
            if (commentable_type === 'issue') {
                getIssueNameById(commentable_id).then(issueName => {
                    path = `/issue/${issueName}/comments/${id}`;
                    setIsLoadingIssueName(false);
                    navigate(path);
                });
            } else {
                path = `/${commentable_type}/${commentable_id}/comments/${id}`;
                setIsLoadingIssueName(false);
                navigate(path);
            }
        } else {
            return;
        }
    };

    const handleReplyClick = async () => {
        if (!currentUser) {
            setMenuOpen(false);
            setDrawerOpen(false);
            setShowAuthModal(true);
        } else {
            setMenuOpen(false);
            setDrawerOpen(false);
            if (location.pathname.includes(`${commentable_type}`)) {
                setCommentAction('reply');
                setIsReplying(true);
            } else {
                setIsLoadingIssueName(true);
                let path;
                if (commentable_type === 'issue') {
                    const issueName = await getIssueNameById(commentable_id);
                    path = `/issue/${issueName}/comments/${id}`;
                } else {
                    path = `/${commentable_type}/${commentable_id}/comments/${id}`;
                }
                setIsLoadingIssueName(false);
                navigate(path);
            }
        }
    };

    const handleEditClick = async () => {
        if (!currentUser) {
            setMenuOpen(false);
            setDrawerOpen(false);
            setShowAuthModal(true);
        } else {
            setMenuOpen(false);
            setDrawerOpen(false);
            if (location.pathname.includes(`${commentable_type}`)) {
                setCommentAction('edit');
                setIsEditing(true);
            } else {
                setIsLoadingIssueName(true);
                let path;
                if (commentable_type === 'issue') {
                    const issueName = await getIssueNameById(commentable_id);
                    path = `/issue/${issueName}/comments/${id}`;
                } else {
                    path = `/${commentable_type}/${commentable_id}/comments/${id}`;
                }
                setIsLoadingIssueName(false);
                navigate(path);
            }
        }
    };

    const handleDeleteClick = () => {
        if (!currentUser) {
            setShowAuthModal(true)
        } else {
            setMenuOpen(false)
            setDrawerOpen(false)
            setIsDeleteDialogOpen(true)
        }
    }

    const handleShare = async () => {
        let url = `${window.location.origin}/${commentable_type}/${commentable_id}/comments/${id}`
        if (commentable_type === 'issue') {
            const issueName = await getIssueNameById(commentable_id);
            url = `${window.location.origin}/issue/${issueName}/comments/${id}`
        } else {
            url = `${window.location.origin}/${commentable_type}/${commentable_id}/comments/${id}`
        }
        navigator.clipboard.writeText(url).then(() => {
            setMenuOpen(false)
            setDrawerOpen(false)
            setAnchorEl(null)
            toast.success('Copied to clipboard!')
        })
    }  

    const handleReportClick = () => {
        if (!currentUser) {
            setShowAuthModal(true)
        } else {
            setMenuOpen(false)
            setDrawerOpen(false)
            setIsReportModalOpen(true)
        }
    }

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const element = event.target as HTMLElement;
        if (
          anchorEl &&
          !moreIconRef.current?.contains(element) &&
          !menuRef.current?.contains(element) &&
          !reportModalRef.current?.contains(element) &&
          !isEditing &&
          !isReplying
        ) {
            setMenuOpen(false)
            setDrawerOpen(false)
        }
      };
  
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [anchorEl, isEditing, isReplying]);


    const handleSaveClick = () => {
        setTimeout(() => {
            setMenuOpen(false)
            setDrawerOpen(false)
        }, 500)
    }

    const handleAddReply = (replyContent: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[]) => {
        if (id) {
            onAddReply(id, replyContent, privacy, coauthors)
            setIsCollapsed(false)
            setIsReplying(false)
        }
    }

    const handleUpdateComment = (updatedContent: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[], addedCoauthors: CoauthorSuggestion[], removedCoauthors: CoauthorSuggestion[]) => {
        if (id) {
            onUpdateComment(id, updatedContent, privacy, coauthors, addedCoauthors, removedCoauthors)
            setIsCollapsed(false)
            setIsEditing(false)
        }
    }

    const confirmDelete = () => {
        if (id) {
            onDeleteComment(id)
            setIsDeleteDialogOpen(false)
        }
    }

    const cancelDelete = () => {
        setIsDeleteDialogOpen(false)
    }

    const handleMouseEnterLine = (parentId: number) => {
        // highlight the parent line
        const parentLine = document.querySelector(`[data-line-id="${parentId}"]`);
        const parentTreeLine = document.querySelector(`[data-tree-line-id="${parentId}"]`);
        if (parentLine && parentTreeLine) {
            //parentTreeLine.classList.add('comment-tree-line-hovered');
        parentTreeLine?.classList.add('comment-tree-line-hovered');
            //parentTreeLine.classList.remove('comment-tree-line-unhovered');
        }      

        // highlight all child branch lines
        const childBranchLines = document.querySelectorAll(`[data-line-parent-id="${parentId}"]`);
        childBranchLines.forEach((el) => el.classList.add('comment-branch-line-hovered'));
        childBranchLines.forEach((el) => el.classList.remove('comment-branch-line-unhovered'));
      };
      
      const handleMouseLeaveLine = (parentId: number) => {
        // remove highlight from the parent line
        const parentLine = document.querySelector(`[data-line-id="${parentId}"]`);
        const parentTreeLine = document.querySelector(`[data-tree-line-id="${parentId}"]`);
        if (parentLine && parentTreeLine) {
            parentTreeLine.classList.remove('comment-tree-line-hovered');
            parentTreeLine.classList.add('comment-tree-line-unhovered');
        }
      
        // remove highlight from the child branch lines
        const childBranchLines = document.querySelectorAll(`[data-line-parent-id="${parentId}"]`);
        childBranchLines.forEach((el) => el.classList.add('comment-branch-line-unhovered'));
        childBranchLines.forEach((el) => el.classList.remove('comment-branch-line-hovered'));
      };

    const MoreButton = React.forwardRef<
        HTMLButtonElement,
        React.ButtonHTMLAttributes<HTMLButtonElement>
    >((props, ref) => (
        <button
            type="button"
            ref={ref}
            {...props}
            className="size-7 rounded-full flex justify-center items-center hover:bg-gray-200 dark:hover:bg-eoe-dark-icon-hover dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover"
            disabled={!is_active}
        >
            <Ellipsis className="icon-stroke size-5"/>
        </button>
    ))
    MoreButton.displayName = 'MoreButton'

    const MoreOptions = () => (
        <>
            {!userIsMobile ? (
            <div className="flex flex-col">
                {isAuthor && currentUser?.id === user_id && (
                    <>
                        <DropdownMenuItem onClick={handleEditClick}>
                            <div className="flex items-center gap-2 px-4 py-2">
                                <Pencil className="size-4 dark:text-eoe-dark-icon-stroke"/>
                                <span>Edit</span>
                            </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleDeleteClick}>
                            <div className="flex items-center gap-2 px-4 py-2">
                                <Trash className="size-4 dark:text-eoe-dark-icon-stroke"/>
                                <span>Delete</span>
                            </div>
                        </DropdownMenuItem>
                    </>
                    )}
                {currentUser && (
                    <div className='relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover focus:bg-eoe-icon-hover dark:focus:bg-eoe-dark-icon-hover focus:text-eoe-text-sub dark:focus:text-eoe-dark-text-sub data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'>
                        <Save 
                            entityId={id} 
                            entityType="comment" 
                            currentUser={currentUser}
                            onSave={handleSaveClick}
                            className="block w-full flex-row items-center gap-2 px-4 py-2 rounded-md"
                        />
                    </div>
                )}
                <DropdownMenuItem onClick={handleShare}>
                    <div className="flex items-center gap-2 px-4 py-2">
                        <LinkIcon className="size-4 dark:text-eoe-dark-icon-stroke"/>
                        <span>Copy link</span>
                    </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleReportClick}>
                    <div className="flex items-center gap-2 px-4 py-2">
                        <Flag className="size-4 dark:text-eoe-dark-icon-stroke"/>
                        <span>Report</span>
                    </div>
                </DropdownMenuItem>
            </div>
            ) : (
                    <>
                        {isAuthor && currentUser?.id === user_id && (
                            <>
                                <button onClick={handleEditClick} className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                                    <Pencil className="size-4"/>
                                    <span>Edit</span>
                                </button>
                                <button onClick={handleDeleteClick} className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                                    <Trash className="size-4"/>
                                    <span>Delete</span>
                                </button>
                            </>
                        )}
                        <>
                        {currentUser && (
                                <Save 
                                    entityId={id} 
                                    entityType="comment" 
                                    currentUser={currentUser}
                                    onSave={handleSaveClick}
                                    className="flex flex-row items-start gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer"
                                />
                        )}
                        </>
                        <button onClick={handleShare} className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                            <LinkIcon className="size-4"/>
                            <span>Copy link</span>
                        </button>
                        <button onClick={handleReportClick} className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                            <Flag className="size-4"/>
                            <span>Report</span>
                        </button>
                    </>
                )}
        </>
    )
    //arbitrary log to clear eslint error
    if (coauthors.length < 0) {
        console.log('newCoauthors', newCoauthors)
        console.log('removedCoauthors', removedCoauthors)
    }

    const handleCommentCancel = () => {
        setCommentAction(null);
        setIsReplying(false);
        setIsEditing(false);
    };

    const renderCommentInputPortal = () => {
        if (!commentAction) return null;
        
        return createPortal(
            commentAction === 'reply' ? (
                <CommentInput
                    onSubmit={handleAddReply}
                    onCancel={handleCommentCancel}
                    buttonText="Reply"
                    action="reply"
                    commentData={{
                        id,
                        user_id,
                        username,
                        content,
                        updated_at,
                        created_at,
                        properties: {
                            coauthors: properties?.coauthors || []
                        }
                    }}
                />
            ) : (
                <CommentInput
                    onSubmit={handleUpdateComment}
                    onCancel={handleCommentCancel}
                    buttonText="Save"
                    initialContent={content}
                    initialPrivacy={properties?.privacy}
                    action="edit"
                    commentData={{
                        id,
                        user_id,
                        username,
                        content,
                        updated_at,
                        created_at,
                        properties: {
                            coauthors: properties?.coauthors || []
                        }
                    }}
                    onCoauthorsChange={(added: any, removed: any) => {
                      setCoauthors(prev => {
                        // Remove coauthors that were removed
                        const filtered = prev.filter(
                          (c: any) => !removed.some((r: any) => parseInt(r.id) === parseInt(c.id) && r.type === c.type)
                        );
                        return filtered;
                      });
                        setNewCoauthors(added);
                        setRemovedCoauthors(removed);
                    }}
                />
            ),
            document.body
        );
    };

    return (
        <div className={`eoe-dark w-full ${depth > 0 ? ' pl-[3px]' : ''} ${isEntitySection ? ' my-4 px-4' : 'card-main'}`} onClick={handleCardClick}>
        {isReportModalOpen && (
          <Report
            entityId={id}
            entityType="comment"
            open={isReportModalOpen}
            onClose={() => setIsReportModalOpen(false)}
          />
        )}
            <div className={`flex space-x-0 w-full ${isEntitySection ? 'card-main-profile-page' : 'card-main my-1 p-4'}`}>
                <div className="flex flex-col items-center">
                    <div className="relative flex items-center justify-center">
                        <div
                            id={`thread-branch-line-${parent_id}`}
                            data-comment-depth={depth}
                            data-comment-id={id}
                            data-comment-parent-id={parent_id}
                            data-comment-root-id={root_id}
                            data-line-parent-id={parent_id}
                            onMouseEnter={parent_id ? () => handleMouseEnterLine(parent_id) : undefined}
                            className={`thread-branch-line cursor-pointer ${depth > 0 ? 'absolute bottom-1/2 right-full h-[8px] w-[15.515px] rounded-bl-[10px] border-0 border-b-[1px] border-l-[1px] border-solid comment-branch-line-unhovered ' : ''}`}
                        ></div>
                        <Avatar
                            className="rounded-full"
                            alt={username !== '[deleted]' ? username || "Profile" : `D`}
                            component={username !== '[deleted]' ? Link : 'span'}
                            to={username !== '[deleted]' ? `/user/${username}` : ''}
                            src={properties_public?.avatar_key ? `${IMAGE_CDN}/${properties_public.avatar_key}` : undefined}
                            sx={{ width: 24, height: 24 }}
                        />
                    </div>
                    {hasReplies && (
                        <div id={`thread-tree-line-${id}`} 
                            data-line-id={id}
                            onMouseEnter={() => handleMouseEnterLine(id)}
                            onMouseLeave={() => handleMouseLeaveLine(id)}
                            className={`group/thread-tree-line cursor-pointer relative w-5 ${!isCollapsed ? 'flex-grow mb-3 mt-[26px] ' : 'flex-grow mb-3 mt-2.5 '}`}>
                            <div 
                                data-tree-line-id={id}
                                className={`absolute ${isCollapsed ? 'bottom-2.5' : 'bottom-[26px]'} left-1/2 h-full w-[1px] -translate-x-1/2 transform comment-tree-line-unhovered`}
                            />
                            <button
                                onClick={() => setIsCollapsed(!isCollapsed)}
                                className="absolute inset-0"
                            >
                                <div
                                    data-tree-line-id={id}
                                    className={`flex absolute ${isCollapsed ? 'bottom-[2.6px]' : 'bottom-[18.75px]'} left-1/2 -translate-x-1/2 translate-y-1/2 transform flex-grow-0 comment-tree-line-unhovered bg-transparent group-hover/thread-tree-line:text-gray-700 dark:group-hover/thread-tree-line:text-white`}
                                >
                                    {isCollapsed ? (
                                        <CirclePlus data-tree-line-id={id} className="size-4 rounded-full" />
                                    ) : (
                                        <CircleMinus data-tree-line-id={id} className="size-4 rounded-full" />
                                    )}
                                </div>
                            </button>
                        </div>
                    )}
                </div>

                <div className={`flex flex-col ml-0 w-full`}>
                    <article className="w-full">
                        <div className={`ignore-card-click grid grid-cols-auto items-center w-full`}>
                            <div className={`group flex items-center gap-1 ml-1 text-sm w-full overflow-hidden`}>
                                {username !== '[deleted]' ? (
                                    <Tooltip title={`View ${username}'s profile`} arrow>
                                        <Link
                                            to={`/user/${username}`}
                                            className="card-username rounded-full group"
                                        >
                                            {username}
                                        </Link>
                                    </Tooltip>
                                ) : (
                                    <span className="card-username rounded-full group">
                                        {username}
                                    </span>
                                )}
                                <EntityPrivacy
                                  privacy={properties.privacy}
                                  viewerRelation={viewer_relation}
                                  onPrivacyChange={() => {}}
                                  actorType="user"
                                  editMode={false}
                                  entityId={id}
                                  entityType="comment"
                                  miniIcon={true}
                                  showDropdown={false}
                                />
                                {coauthors.length > 0 && (
                                    <button onClick={() => setShowCoauthorsModal(true)} className="flex flex-row items-center gap-1 text-xs shrink-0 bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover px-1 py-1 rounded-full cursor-pointer">
                                        <BookCheck className="size-3.5"/> {coauthors.length}
                                    </button>
                                )}
                                <Tooltip title={dayjs(created_at).format('LLL')} arrow>
                                    <span className="card-date text-xs shrink-0 mr-2">
                                        {formatRelativeTime(created_at)}
                                    </span>
                                </Tooltip>
                            </div>
                        </div>
                        {!isEditing && (
                            <>
                                <Box className="comment-content w-full mx-1 my-1 text-sm">
                                    {transformHtmlContent(content)}
                                </Box>
                                <div className="flex flex-row items-center mr-0 min-w-[174px] mt-0">
                                    <EntityActions
                                        entity={entity}
                                        entityType="comment"
                                    />
                                    <button
                                        className="ignore-card-click text-xs flex cursor-pointer items-center justify-center gap-1 rounded-full px-2 py-1 font-semibold transition-colors hover:bg-gray-200 dark:hover:bg-eoe-dark-icon-hover dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover"
                                        onClick={handleReplyClick}
                                        disabled={isLoadingIssueName}
                                    >
                                        <MessageSquareReply className="size-5" />
                                        <span className="text-xs">{isLoadingIssueName ? 'Loading...' : 'Reply'}</span>
                                    </button>
                                    <div className="icon-more text-xl">
                                    {userIsMobile ? (
                                        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                                            <DrawerTrigger asChild>
                                                <MoreButton ref={moreIconRef} />
                                            </DrawerTrigger>
                                            <DrawerContent title="Actions">
                                                <div className="flex flex-col">
                                                    <MoreOptions />
                                                </div>
                                            </DrawerContent>
                                        </Drawer>
                                    ) : (
                                        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                                            <DropdownMenuTrigger asChild>
                                                <MoreButton ref={moreIconRef} />
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <MoreOptions />
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                    </div>
                                </div>
                            </>
                        )}
                    </article>
                    {/* Reply input is now handled through the portal */}
                    {renderCommentInputPortal()}
                    {hasReplies && !isCollapsed && (
                        <div className="comment-content card-description text-xs w-full">
                            <RenderComments
                                comments={replies}
                                depth={depth + 1}
                                maxDepth={maxDepth}
                                isAllCollapsed={isAllCollapsed}
                                onAddReply={onAddReply}
                                onUpdateComment={onUpdateComment}
                                onDeleteComment={onDeleteComment}
                                commentable_type={commentable_type}
                                commentable_id={commentable_id}
                                isEntitySection={isEntitySection}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isDeleteDialogOpen && (
                <DialogConfirmation 
                    title="Delete Comment" 
                    description="Are you sure you want to delete this comment?" 
                    isOpen={isDeleteDialogOpen} 
                    onConfirm={confirmDelete} 
                    onCancel={cancelDelete}
                />
            )}
            {renderCommentInputPortal()}
            {showCoauthorsModal && (
                <CoauthorsModal
                    entityId={id}
                    entityType="comment"
                    onClose={() => setShowCoauthorsModal(false)}
                />
            )}
        </div>
    )
}

export default Comment
